import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import LucideIconByName from "@/components/LucideIconByName";

/** Big rank badge with progress bar to next tier, used on profile pages. */
export default function UserClassBadge({ userId }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!userId) return;
    setLoading(true);
    api.get("/users/cards", { params: { ids: userId } })
      .then(({ data }) => { if (alive) setCard(data?.[userId] || null); })
      .catch(() => { if (alive) setCard(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  if (loading) {
    return (
      <section className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4 flex items-center gap-2 text-xs text-[var(--tt-text-muted)]">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading rank...
      </section>
    );
  }
  if (!card?.user_class) return null;
  const uc = card.user_class;

  return (
    <section
      data-testid="user-class-badge"
      className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5"
      style={{ borderLeft: `4px solid ${uc.color}` }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: `${uc.color}22`,
            border: `1px solid ${uc.color}66`,
            boxShadow: `0 4px 18px ${uc.color}33`,
          }}
        >
          <LucideIconByName name={uc.icon} className="w-6 h-6" color={uc.color} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-faint)]">
            User Class
          </div>
          <div className="font-display text-xl font-extrabold tracking-tight" style={{ color: uc.color }}>
            {uc.name}
          </div>
          <div className="text-[11px] text-[var(--tt-text-muted)] mt-0.5">
            {uc.posts} posts · {uc.member_for_days} days on TorHub
          </div>
        </div>
      </div>
      {uc.next_name && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-[var(--tt-text-muted)] mb-1.5">
            <span>Progress to <span style={{ color: uc.color }}>{uc.next_name}</span></span>
            <span className="font-mono-tt">{uc.progress_pct}%</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${uc.progress_pct}%`,
                background: `linear-gradient(90deg, ${uc.color}, ${uc.color}cc)`,
              }}
            />
          </div>
          <div className="text-[10px] text-[var(--tt-text-faint)] mt-1">
            {Math.max(0, uc.next_threshold - uc.score)} more activity points to level up
          </div>
        </div>
      )}
      {!uc.next_name && (
        <div className="mt-3 text-[11px] text-[var(--tt-yellow)] font-bold uppercase tracking-wider">
          🏆 Max tier reached — TorHub Legend.
        </div>
      )}
    </section>
  );
}
